import { useEffect, useMemo } from 'react';

import { GuardianRelation } from '@dreryk/egabinet-patient-frontend-api';
import { Pesel } from '@dreryk/personal-external-identifier-common-api';

import { useFormController } from '@dreryk/react-common';
import { ModalCancelButton, ModalCloseButton, RadioGroup } from '@dreryk/react-components';

import { guardiansTranslationKeys } from '../../translations';
import { GuardianExternalIdentifierType } from '../PersonForm/GuardianExternalIdentifier/GuardianExternalIdentifierTypeField';

import { GUARDIAN_FORM_INITIAL_VALUES, STATUTORY_REPRESENTATIVE_RADIO_ITEMS } from './GuardianModal.constants';
import { GuardianModalFormBean, StatutoryRepresentative } from './GuardianModal.types';
import { GuardianModalConfirmButton } from './GuardianModalConfirmButton/GuardianModalConfirmButton';
import { PatientAutosuggest } from './PatientAutosuggest/PatientAutosuggest';
import { StyledGuardianModalContent } from './StyledGuardianModalContent';
import { getGuardianGender, getInitialValues } from './GuardianModalForm.utils';
import { useRelationCodesRadioItems } from './GuardianModal.hooks';

export type GuardianModalFormProps = {
   readonly initialValues?: GuardianModalFormBean;
   readonly onGuardianDetailsToggleVisibility: VoidFunction;
   readonly onCancel: VoidFunction;
   readonly onSubmit?: (formData: GuardianModalFormBean) => void;
   readonly guardianId?: number;
   readonly patientId: number | undefined;
   readonly patientPesel: Pesel | null | undefined;
   readonly isIdentifyingGuardian?: boolean;
   readonly statutoryRepresentative?: boolean;
   onSuccess?: (parameters: GuardianModalFormBean) => void;
};

// eslint-disable-next-line max-lines-per-function
export const GuardianModalForm = (props: GuardianModalFormProps): JSX.Element => {
   const { initialValues } = props;
   const {
      register,
      watch,
      setValue,
      getValue,
      trigger,
      handleSubmit,
      setError,
      formState,
   } = useFormController<GuardianModalFormBean>({
      ...GUARDIAN_FORM_INITIAL_VALUES,
      ...getInitialValues(initialValues, props.statutoryRepresentative),
   });
   const statutoryRepresentative = StatutoryRepresentative.YES === watch('statutoryRepresentative');
   const currentGuardian = watch('guardian');
   const currentRelationCode = watch('relationCode');
   const gender = getGuardianGender(
      currentGuardian?.guardianExternalIdentifierType as GuardianExternalIdentifierType,
      currentGuardian?.guardianExternalIdentifierValue,
      currentGuardian?.gender ?? undefined
   );

   useEffect(() => {
      if (gender === 'MAN' && currentRelationCode === 'MOTHER') {
         setValue('relationCode', 'FATHER');
      }
      if (gender === 'WOMAN' && currentRelationCode === 'FATHER') {
         setValue('relationCode', 'MOTHER');
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [gender]);

   const relationCodeItems = useRelationCodesRadioItems(gender);

   const editMode = useMemo(() => {
      return Boolean(initialValues);
   }, [initialValues]);

   return (
      <StyledGuardianModalContent>
         <PatientAutosuggest
            onGuardianDetailsModalVisibilityToggle={props.onGuardianDetailsToggleVisibility}
            statutoryRepresentative={statutoryRepresentative}
            register={register}
            watch={watch}
            setValue={setValue}
            editMode={editMode}
            patientId={props.patientId}
            patientPesel={props.patientPesel}
            identifyingWithGuardian={props.isIdentifyingGuardian}
         />
         <RadioGroup<GuardianRelation>
            testId={'guardianRelationCode'}
            items={relationCodeItems}
            controller={register('relationCode')}
            label={guardiansTranslationKeys.relation}
            horizontal
         />
         <RadioGroup<StatutoryRepresentative>
            testId='statutoryRepresentative'
            items={STATUTORY_REPRESENTATIVE_RADIO_ITEMS}
            controller={register('statutoryRepresentative')}
            label={guardiansTranslationKeys.statutoryRepresentative}
            horizontal
            disabled={Boolean(props.isIdentifyingGuardian)}
         />
         <GuardianModalConfirmButton
            patientId={editMode ? undefined : props.patientId}
            guardianId={props.guardianId}
            watch={watch}
            setValue={setValue}
            getValue={getValue}
            setError={setError}
            handleSubmit={handleSubmit}
            trigger={trigger}
            initialValues={watch('guardian')}
            onSubmit={props.onSubmit}
            onSuccess={props.onSuccess}
         />
         <ModalCancelButton onClick={props.onCancel} shouldConfirmExit={formState.isDirty} />
         <ModalCloseButton onClick={props.onCancel} shouldConfirmExit={formState.isDirty} />
      </StyledGuardianModalContent>
   );
};
